import { useRef, useState } from 'react'
import { message } from 'antd'
import { formatCurrency } from '@sportninja/common/currency'
import {
  previewAdditionalInvoiceRemoval,
  previewOfficialAmount,
  previewOfficialLine,
} from '@sportninja/common/services/teamBalance'
import type {
  BalanceBillingSchedule,
  OfficialLinePreview,
  OfficialRemovalPreview,
  TeamBalancePayload,
} from '@sportninja/common/services/teamBalance'
import type { BalanceCandidateTableRow } from '../types/balanceGrid'
import type {
  OfficialEffectState,
  OfficialEffectTarget,
} from '../types/officialEffect'
import { balanceText } from '../utils/balanceI18n'
import {
  officialWriteError,
  officialWriteMessage,
} from '../utils/officialWriteError'

type Mutation<Variables> = {
  mutateAsync: (variables: Variables) => Promise<TeamBalancePayload>
  isPending: boolean
}

export interface OfficialLineMutations {
  updateSlotAmount: Mutation<{
    slotId: string
    amount: number
    previewFingerprint?: string
    expectedKind?: 'player' | 'official'
  }>
  createOfficialLine: Mutation<{
    registrationId: string
    payerUserId: string
    teamOfficialUid: string | null
    amount: number
    previewFingerprint: string
  }>
  removeAddedInvoice: Mutation<{
    slotId: string
    billUid: string
    expectedKind?: 'player' | 'official'
    previewFingerprint?: string
  }>
}

interface UseOfficialLineFlowsOptions {
  teamId: string
  rosterId: string
  mutations: OfficialLineMutations
  /** Re-read the sheet — after a refusal that means the page no longer matches. */
  refetch: () => unknown
}

/**
 * Sep 23 — every change to a team official's line is PREVIEWED, then CONFIRMED with
 * the preview's fingerprint (money rules §4). This hook owns that round trip for the
 * sheet, so BalanceTab only renders what it says:
 *
 *   1. preview the change (a candidate's new line, a line's new amount, or undoing an
 *      official's added invoice);
 *   2. when the preview changes any uninvoiced player share — and always for an undo —
 *      ask first (`state`), listing each share's before and after; otherwise commit
 *      straight away;
 *   3. commit with the fingerprint. `preview_changed` re-reads the preview and asks
 *      again, flagged as changed (the sheet itself refreshes in the mutation layer);
 *      any other refusal is reported and closes.
 *
 * One change at a time (review round 1, F1): from its preview until its write lands, or
 * its dialog closes, another official edit is refused out loud — it writes nothing, and
 * it can never replace or clear the first change's dialog.
 *
 * A write that frees money says so ("$X is now unallocated") only when it did.
 */
export const useOfficialLineFlows = ({
  teamId,
  rosterId,
  mutations,
  refetch,
}: UseOfficialLineFlowsOptions) => {
  'use memo'
  const [state, setState] = useState<OfficialEffectState | null>(null)
  // The change in progress, from its preview to its write (or its dialog closing). A ref,
  // so two edits committed in the same tick still see each other.
  const activeRef = useRef(false)
  // A second click lands before `confirming` re-renders the button disabled.
  const committingRef = useRef(false)

  const finish = () => {
    activeRef.current = false
  }

  const previewFor = (
    target: OfficialEffectTarget
  ): Promise<OfficialLinePreview | OfficialRemovalPreview> => {
    if (target.kind === 'amount') {
      return previewOfficialAmount(
        teamId,
        rosterId,
        target.slotId,
        target.amount
      )
    }
    if (target.kind === 'create') {
      return previewOfficialLine(teamId, rosterId, {
        team_registration_id: target.row.registration.id,
        payer_user_id: target.row.candidate.payer_user_id,
        team_official_id: target.row.candidate.team_official_uid,
        amount: target.amount,
      })
    }

    return previewAdditionalInvoiceRemoval(
      teamId,
      rosterId,
      target.slotId,
      target.bill.uid,
      'official'
    )
  }

  const commit = (
    target: OfficialEffectTarget,
    fingerprint: string
  ): Promise<TeamBalancePayload> => {
    if (target.kind === 'amount') {
      return mutations.updateSlotAmount.mutateAsync({
        slotId: target.slotId,
        amount: target.amount,
        previewFingerprint: fingerprint,
        expectedKind: 'official',
      })
    }
    if (target.kind === 'create') {
      return mutations.createOfficialLine.mutateAsync({
        registrationId: target.row.registration.id,
        payerUserId: target.row.candidate.payer_user_id,
        teamOfficialUid: target.row.candidate.team_official_uid,
        amount: target.amount,
        previewFingerprint: fingerprint,
      })
    }

    return mutations.removeAddedInvoice.mutateAsync({
      slotId: target.slotId,
      billUid: target.bill.uid,
      expectedKind: 'official',
      previewFingerprint: fingerprint,
    })
  }

  const reportFailure = (error: unknown) => {
    message.error(officialWriteMessage(error))
    const kind = officialWriteError(error)
    if (kind === 'identity_changed' || kind === 'refresh_required') {
      refetch()
    }
  }

  const succeed = (
    target: OfficialEffectTarget,
    result: TeamBalancePayload | undefined
  ) => {
    setState(null)
    if (target.kind === 'removal') {
      message.success(balanceText('common:balanceRemoveAddedInvoiceSuccess'))
    }
    const freedCents = result?.rebalance?.freed_to_unallocated_cents ?? 0
    if (freedCents > 0) {
      message.info(
        balanceText('common:balanceOfficialNowUnallocated', {
          amount: formatCurrency({
            amount: freedCents,
            currencyCode: target.currencyCode,
          }),
        })
      )
    }
  }

  const reshow = async (target: OfficialEffectTarget) => {
    try {
      const preview = await previewFor(target)
      setState({ target, preview, changed: true })
    } catch (error) {
      finish()
      setState(null)
      reportFailure(error)
    }
  }

  const commitOrReshow = async (
    target: OfficialEffectTarget,
    fingerprint: string
  ) => {
    if (committingRef.current) {
      return
    }
    committingRef.current = true
    try {
      const result = await commit(target, fingerprint)
      finish()
      succeed(target, result)
    } catch (error) {
      if (officialWriteError(error) === 'preview_changed') {
        // Still the same change: it asks again with the current effect.
        await reshow(target)
        return
      }
      finish()
      setState(null)
      reportFailure(error)
    } finally {
      committingRef.current = false
    }
  }

  const start = async (target: OfficialEffectTarget) => {
    if (activeRef.current) {
      message.error(balanceText('common:balanceOfficialBusy'))
      return
    }
    activeRef.current = true

    let preview: OfficialLinePreview | OfficialRemovalPreview
    try {
      preview = await previewFor(target)
    } catch (error) {
      finish()
      reportFailure(error)
      return
    }

    const movesPlayers = preview.player_pool_effect.lines.length > 0
    if (target.kind !== 'removal' && !movesPlayers) {
      await commitOrReshow(target, preview.fingerprint)
      return
    }
    setState({ target, preview, changed: false })
  }

  return {
    state,
    confirming:
      mutations.updateSlotAmount.isPending ||
      mutations.createOfficialLine.isPending ||
      mutations.removeAddedInvoice.isPending,
    /** A typed amount on an official's line. */
    submitAmount: (
      slotId: string,
      name: string,
      amount: number,
      currencyCode: string
    ) => start({ kind: 'amount', slotId, name, amount, currencyCode }),
    /** A typed amount on a team official's $0 candidate row: opens their line. */
    submitCandidate: (row: BalanceCandidateTableRow, amount: number) =>
      start({
        kind: 'create',
        row,
        amount,
        currencyCode: row.registration.currency?.code ?? 'CAD',
      }),
    /** Undo an official's added invoice (always asks first). */
    startRemoval: (
      slotId: string,
      name: string,
      bill: BalanceBillingSchedule,
      currencyCode: string
    ) => start({ kind: 'removal', slotId, name, bill, currencyCode }),
    confirm: () => {
      if (state) {
        commitOrReshow(state.target, state.preview.fingerprint)
      }
    },
    close: () => {
      // Its write is already on its way: the dialog closes itself when that lands.
      if (committingRef.current) {
        return
      }
      setState(null)
      finish()
    },
  }
}

export default useOfficialLineFlows
